import { z } from 'zod';
import type { ResourceKind } from './dto.js';

/**
 * Usage metrics contract (ADR-0012): CPU/memory collected per server from
 * Coolify's Sentinel agent, rolled up hourly per resource, aggregated per
 * subscription, and surfaced as charts.
 *
 * Availability is a first-class part of the contract: Sentinel collects no
 * per-container metrics for docker-compose applications or service
 * templates (upstream gap), and collection needs Sentinel enabled plus a
 * readable token per server. Readers must render those states explicitly —
 * "metrics unavailable" is an answer, a fabricated zero is not.
 */

// ── Availability ────────────────────────────────────────────────────

export const METRICS_AVAILABILITIES = ['available', 'unsupported', 'pending'] as const;
export type MetricsAvailability = (typeof METRICS_AVAILABILITIES)[number];

// ── Series ──────────────────────────────────────────────────────────

/** One hourly rollup bucket (UTC hour start). */
export interface MetricPointDto {
  /** ISO timestamp of the bucket's hour start. */
  bucket: string;
  cpuAvgPercent: number;
  cpuMaxPercent: number;
  memoryAvgMb: number;
  memoryMaxMb: number;
  sampleCount: number;
}

/** Most recent raw sample for a resource. */
export interface MetricSampleDto {
  sampledAt: string;
  cpuPercent: number;
  memoryMb: number;
}

export interface ResourceMetricsDto {
  resourceId: string;
  availability: MetricsAvailability;
  /** Human-readable explanation when not `available`. */
  reason: string | null;
  latest: MetricSampleDto | null;
  series: MetricPointDto[];
}

/** Per-resource availability line item inside a subscription aggregate. */
export interface SubscriptionMetricsResourceDto {
  resourceId: string;
  name: string;
  kind: ResourceKind;
  availability: MetricsAvailability;
  reason: string | null;
}

export interface SubscriptionMetricsDto {
  subscriptionId: string;
  /** Hourly buckets summed across the subscription's available resources. */
  series: MetricPointDto[];
  resources: SubscriptionMetricsResourceDto[];
  /** Sum of fresh latest-sample memory across resources; null when none. */
  latestMemoryMb: number | null;
}

// ── Servers (Infrastructure page) ───────────────────────────────────

export interface ServerMetricsStateDto {
  serverUuid: string;
  serverName: string;
  sentinelEnabled: boolean;
  metricsEnabled: boolean;
  /** False when GET /servers/{uuid} hides the token (needs read:sensitive). */
  tokenAvailable: boolean;
  refreshRateSeconds: number | null;
  historyDays: number | null;
  lastRunAt: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
  /** Supported resources on this server (collection candidates). */
  containersMonitored: number;
}

export interface MetricsStatusDto {
  servers: ServerMetricsStateDto[];
  running: boolean;
  intervalMs: number;
  retentionDays: number;
  /** An SSH private key is configured (Settings → Metrics). */
  sshKeyConfigured: boolean;
}

/** Panel-wide dashboard aggregate: managed resources + server system load. */
export interface MetricsOverviewDto {
  /** Hourly buckets summed across every available managed resource. */
  resources: MetricPointDto[];
  servers: Array<{
    serverUuid: string;
    serverName: string;
    series: MetricPointDto[];
  }>;
}

// ── Inputs ──────────────────────────────────────────────────────────

export const METRICS_RANGES = ['24h', '7d', '30d'] as const;
export type MetricsRange = (typeof METRICS_RANGES)[number];

export const metricsRangeSchema = z.object({
  range: z.enum(METRICS_RANGES).default('24h'),
});

/** Hours of history each range covers. */
export const METRICS_RANGE_HOURS: Record<MetricsRange, number> = {
  '24h': 24,
  '7d': 24 * 7,
  '30d': 24 * 30,
};

/**
 * Enablement toggles forwarded to Coolify's PATCH /servers/{uuid}. Coolify
 * v4.1.2 rejects these fields (enablement is UI-only upstream, ADR-0012);
 * the panel forwards them anyway for forward compatibility and maps the
 * rejection to actionable guidance.
 */
export const updateServerSentinelSchema = z
  .object({
    sentinelEnabled: z.boolean().optional(),
    metricsEnabled: z.boolean().optional(),
  })
  .refine((v) => v.sentinelEnabled !== undefined || v.metricsEnabled !== undefined, {
    message: 'Provide sentinelEnabled and/or metricsEnabled',
  });
export type UpdateServerSentinelInput = z.infer<typeof updateServerSentinelSchema>;

export const metricsSettingsSchema = z.object({
  /** OpenSSH/PEM private key for the panel's server-access key (ADR-0012). */
  sshPrivateKey: z.string().trim().min(1).max(16_384),
});
export type MetricsSettingsInput = z.infer<typeof metricsSettingsSchema>;
